import React, { useState, useEffect } from 'react';
import { UserPlus, Shield, Mail, Trash2 } from 'lucide-react';
import api from '../api';

const PermissionManagement = ({ onClose, activeRole }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [diplomas, setDiplomas] = useState([]);
  const [selectedDiploma, setSelectedDiploma] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const getAvailableRoles = () => {
    if (activeRole === 'CG') return [
      { value: 'CO', label: 'Coordinador Operativo' },
      { value: 'ACG', label: 'Auxiliar de la Coordinadora General' },
      { value: 'CA', label: 'Coordinadora Académica' }
    ];
    if (activeRole === 'CO') return [{ value: 'ACO', label: 'Auxiliar de la Coordinadora Operativa' }];
    if (activeRole === 'ACO') return [{ value: 'CD', label: 'Coordinador de Diplomatura' }];
    if (activeRole === 'CD') return [
      { value: 'RM', label: 'Referente Municipal' },
      { value: 'DO', label: 'Docente' },
      { value: 'AL', label: 'Alumno' }
    ];
    return [];
  };

  const availableRoles = getAvailableRoles();

  useEffect(() => {
    fetchPermissions();
    fetchUsers();
    fetchDiplomas();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('users/');
      setUsers(res.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchDiplomas = async () => {
    try {
      const res = await api.get('diplomas/');
      setDiplomas(res.data);
    } catch (err) {
      console.error('Error fetching diplomas:', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await api.get('assign/');
      setPermissions(res.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!email || !role) return;
    if ((role === 'RM' || role === 'DO') && !selectedDiploma) {
      setMessage({ type: 'error', text: 'Debe especificar una diplomatura para este rol' });
      return;
    }
    
    setLoading(true);
    setMessage('');
    try {
      const res = await api.post('assign/', { email, role, diploma: selectedDiploma });
      setMessage({ type: 'success', text: res.data.status });
      setEmail('');
      setRole('');
      setSelectedDiploma('');
      fetchPermissions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Error al asignar rol' });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (assignment) => {
    if (!window.confirm(`¿Desea revocar el permiso para ${assignment.email}?`)) return;
    try {
      await api.post(`assign/${assignment.id}/revoke/`, { type: assignment.type });
      setPermissions(permissions.filter(p => !(p.id === assignment.id && p.type === assignment.type)));
      setMessage({ type: 'success', text: 'Permiso revocado correctamente' });
    } catch (err) {
      console.error('Error revoking permission:', err);
      setMessage({ type: 'error', text: 'Error al revocar el permiso' });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content glassmorphism manage-permissions-modal animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="modal-header d-flex justify-content-between align-items-center mb-4">
          <h2 className="m-0 d-flex align-items-center gap-2">
            <Shield className="text-primary" /> Reconocimiento de Dependientes
          </h2>
          <button className="btn-close ms-2" onClick={onClose}></button>
        </div>

        <div className="assignment-form mb-5">
          <form onSubmit={handleAssign} className="row g-3">
            <div className="col-md-5">
              <label className="form-label">Correo Electrónico</label>
              <div className="input-group">
                <span className="input-group-text"><Mail size={18} /></span>
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="usuario@ejemplo.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  list="existing-emails"
                  required
                />
                <datalist id="existing-emails">
                  {users.map(u => (
                    <option key={u.id} value={u.email} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className={`col-md-${(role === 'RM' || role === 'DO') ? '3' : '4'}`}>
              <label className="form-label">Rol a Asignar</label>
              <select 
                className="form-select" 
                value={role} 
                onChange={e => setRole(e.target.value)}
                required
              >
                <option value="">Seleccione un rol...</option>
                {availableRoles.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            {(role === 'RM' || role === 'DO') && (
              <div className="col-md-3">
                <label className="form-label">Diplomatura</label>
                <select 
                  className="form-select" 
                  value={selectedDiploma} 
                  onChange={e => setSelectedDiploma(e.target.value)}
                  required
                >
                  <option value="">Seleccione diplomatura...</option>
                  {diplomas.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className={`col-md-${(role === 'RM' || role === 'DO') ? '1' : '3'} d-flex align-items-end`}>
              <button 
                type="submit" 
                className="btn btn-primary-gradient w-100 d-flex align-items-center justify-content-center gap-2 px-0"
                disabled={loading}
              >
                <UserPlus size={18} /> {loading ? '' : 'Reconocer'}
              </button>
            </div>
          </form>
          {message && (
            <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mt-3`}>
              {message.text}
            </div>
          )}
        </div>

        <div className="mt-5">
          <h3 className="h6 mb-3 fw-bold text-muted border-bottom pb-2">Resultados del Reconocimiento</h3>
          <div className="table-responsive">
            <table className="table table-hover premium-table align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Diplomatura</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {permissions.length > 0 ? (
                  permissions.map((p, idx) => (
                    <tr key={`${p.type}-${p.id}-${idx}`}>
                      <td>
                        <div className="fw-medium text-start">{p.email}</div>
                      </td>
                      <td>
                        <span className="badge bg-primary bg-opacity-10 text-primary">
                          {p.role}
                        </span>
                      </td>
                      <td className="small text-start">
                        {p.diploma_name || '-'}
                      </td>
                      <td>
                        <span className={`badge ${p.status === 'Activo' ? 'bg-success' : 'bg-warning'} bg-opacity-10 ${p.status === 'Activo' ? 'text-success' : 'text-warning'}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {new Date(p.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-end">
                        <button 
                          onClick={() => handleRevoke(p)}
                          className="btn btn-sm btn-outline-danger border-0"
                          title="Revocar Permiso"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4 small">
                      No hay reconocimientos registrados bajo su autoría
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionManagement;
